import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { FaArrowLeft } from "react-icons/fa";
import Sidebar from "../component/Sidebar";
import Pagination from "./Pagination";
import CollapsibleHeader from "../component/CollapsibleHeader";

// HELPER: Format YYYY-MM-DD to DD-MM-YYYY for display
const formatToDDMMYYYY = (dateStr) => {
  if (!dateStr) return "--";
  const [y, m, d] = dateStr.split("-");
  return `${d}-${m}-${y}`;
};

const LeaveRecord = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // 1. INITIALIZE: Load from Local Storage or default to current date
  const [dateFrom, setDateFrom] = useState(() => 
    localStorage.getItem("leaveRecord_dateFrom") || new Date().toISOString().slice(0, 10)
  );
  const [dateTo, setDateTo] = useState(() => 
    localStorage.getItem("leaveRecord_dateTo") || new Date().toISOString().slice(0, 10)
  );

  const [searchTerm, setSearchTerm] = useState("");
  const [leaveTypeFilter, setLeaveTypeFilter] = useState(""); 
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  // 2. PERSISTENCE: Save dates to Local Storage whenever they change
  useEffect(() => {
    localStorage.setItem("leaveRecord_dateFrom", dateFrom);
    localStorage.setItem("leaveRecord_dateTo", dateTo);
  }, [dateFrom, dateTo]);

  const fetchLeaveRecords = async () => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    try {
      const res = await axios.get("/api/Leave-Records/get-data", {
        params: { fromDate: dateFrom, toDate: dateTo },
      });
      setRows(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setRows([]);
      toast.error("Failed to fetch leave records");
    } finally {
      setLoading(false);
    }
  };

  // Automatically fetch when dates change (Fetch button removed)
  useEffect(() => {
    fetchLeaveRecords();
  }, [dateFrom, dateTo]);

  const filteredRows = useMemo(() => {
    const searchValue = searchTerm.trim().toUpperCase();
    const typeValue = leaveTypeFilter.toUpperCase();

    return rows.filter((row) => {
      const matchesSearch = !searchValue || 
        [row.employeeId, row.employeeName, row.leaveType]
          .map((value) => String(value || "").toUpperCase())
          .some((value) => value.includes(searchValue));

      const matchesType = !typeValue || String(row.leaveType || "").toUpperCase().includes(typeValue);

      return matchesSearch && matchesType;
    });
  }, [rows, searchTerm, leaveTypeFilter]);

  const startIndex = perPage === "all" ? 0 : (currentPage - 1) * perPage;
  const paginatedRows =
    perPage === "all" ? filteredRows : filteredRows.slice(startIndex, startIndex + perPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, leaveTypeFilter, perPage, rows]);

  const handleExportExcel = () => {
    if (filteredRows.length === 0) {
      toast.error("No leave records available to export");
      return;
    }

    const exportData = filteredRows.map((row) => ({
      "Employee ID": row.employeeId || "--",
      Name: row.employeeName || "--",
      "Leave Type": row.leaveType || "--",
      Allowed: row.allowedDays ?? 0,
      Used: row.usedDays ?? 0,
      Balance: row.balanceDays ?? 0,
      Applications: row.applicationCount ?? 0,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Leave Record");
    // Filename also uses the DD-MM-YYYY format
    XLSX.writeFile(workbook, `Leave_Record_${formatToDDMMYYYY(dateFrom)}_to_${formatToDDMMYYYY(dateTo)}.xlsx`);
    toast.success("Leave record exported successfully");
  };

  const filterInputClass = "h-8 w-full rounded border border-dorika-blue bg-white px-3 py-1 text-sm focus:outline-none cursor-pointer font-medium";

  return (
    <div className="flex h-screen flex-col overflow-hidden md:flex-row">
      <Sidebar />

      <div className="page-panel">
        <div className="page-panel rounded-md bg-white p-3 shadow-md">
          <div className="page-sticky-header mb-3 bg-white pb-2">
            <div className="page-title-bar mb-3 flex items-center justify-between">
              <h2 className="text-xl font-bold text-dorika-blue">Leave Record</h2>
              <button
                type="button"
                onClick={() => window.history.back()}
                className="inline-flex h-8 w-[110px] items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 text-sm font-semibold text-white shadow transition hover:bg-blue-800"
              >
                <FaArrowLeft />
                <span>Back</span>
              </button>
            </div>

            <CollapsibleHeader title="Leave Record Filters" contentClassName="mt-3">
              <div className="page-title-bar">
                <div className="flex flex-wrap items-end gap-3 xl:flex-nowrap">
                  <div className="field-stack-mobile min-w-[200px] flex-1">
                    <label className="text-xs font-bold">Search</label>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
                      placeholder="Search Name / Emp ID"
                      className={filterInputClass}
                    />
                  </div>

                  <div className="field-stack-mobile w-32">
                    <label className="text-xs font-bold">Leave Type</label>
                    <select
                      value={leaveTypeFilter}
                      onChange={(e) => setLeaveTypeFilter(e.target.value)}
                      className={filterInputClass}
                    >
                      <option value="">ALL</option>
                      <option value="EL">EL</option>
                      <option value="CL">CL</option>
                      <option value="SL">SL</option>
                    </select>
                  </div>

                  {/* 3. DD-MM-YYYY Date Inputs with Hidden Picker Pattern */}
                  <div className="field-stack-mobile w-40">
                    <label className="text-xs font-bold">From Date</label>
                    <input
                      type="date"
                      id="fromPicker"
                      className="hidden"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                    <input
                      type="text"
                      readOnly
                      value={formatToDDMMYYYY(dateFrom)}
                      onClick={() => document.getElementById('fromPicker').showPicker()}
                      className={filterInputClass}
                    />
                  </div>

                  <div className="field-stack-mobile w-40">
                    <label className="text-xs font-bold">To Date</label>
                    <input
                      type="date"
                      id="toPicker"
                      className="hidden"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                    <input
                      type="text"
                      readOnly
                      value={formatToDDMMYYYY(dateTo)}
                      onClick={() => document.getElementById('toPicker').showPicker()}
                      className={filterInputClass}
                    />
                  </div>

                  <div className="field-stack-mobile w-28">
                    <label className="text-xs font-bold">Show</label>
                    <select
                      value={perPage}
                      onChange={(e) => {
                        const value = e.target.value;
                        setPerPage(value === "all" ? "all" : Number(value));
                      }}
                      className={filterInputClass}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value="all">ALL</option>
                    </select>
                  </div>

                  <div className="field-stack-mobile w-36">
                    <label>&nbsp;</label>
                    <button
                      type="button"
                      onClick={handleExportExcel}
                      className="h-8 rounded bg-green-600 px-4 text-xs font-bold uppercase text-white shadow-md hover:bg-green-700 w-full transition-colors"
                    >
                      Export Excel
                    </button>
                  </div>
                </div>
              </div>
            </CollapsibleHeader>
          </div>

          <div className="table-scroll-area border shadow">
            <table className="table-fixed-header w-full border-collapse text-xs sm:text-sm">
              <thead className="bg-dorika-blue text-white">
                <tr>
                  <th className="border border-dorika-blue px-2 py-2 w-12">SL</th>
                  <th className="border border-dorika-blue px-2 py-2">Employee ID</th>
                  <th className="border border-dorika-blue px-2 py-2">Name</th>
                  <th className="border border-dorika-blue px-2 py-2">Leave Type</th>
                  <th className="border border-dorika-blue px-2 py-2 text-center">Allowed</th>
                  <th className="border border-dorika-blue px-2 py-2 text-center">Used</th>
                  <th className="border border-dorika-blue px-2 py-2 text-center text-green-100 font-bold">Balance</th>
                  <th className="border border-dorika-blue px-2 py-2 text-center">Apps</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="border px-3 py-10 text-center text-gray-500 italic">
                      Loading leave records...
                    </td>
                  </tr>
                ) : paginatedRows.length > 0 ? (
                  paginatedRows.map((row, index) => (
                    <tr key={index} className="odd:bg-white even:bg-dorika-blueLight/40 hover:bg-blue-50 transition-colors">
                      <td className="border border-dorika-blue px-2 py-2 text-center">
                        {perPage === "all" ? index + 1 : startIndex + index + 1}
                      </td>
                      <td className="border border-dorika-blue px-2 py-2">{row.employeeId || "--"}</td>
                      <td className="border border-dorika-blue px-2 py-2">{row.employeeName || "--"}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center font-semibold text-indigo-700">{row.leaveType || "--"}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center">{row.allowedDays ?? 0}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center">{row.usedDays ?? 0}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center font-bold text-green-700">{row.balanceDays ?? 0}</td>
                      <td className="border border-dorika-blue px-2 py-2 text-center">{row.applicationCount ?? 0}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="border px-3 py-10 text-center text-gray-500">
                      No approved leave records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {perPage !== "all" && (
            <Pagination
              total={filteredRows.length}
              perPage={perPage}
              currentPage={currentPage}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveRecord;